import React, { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import { useFormik } from "formik";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { useRecoilValue } from "recoil";
import { authState } from "../../Recoil/RecoilState";
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const makeInitial = (filter) => ({
  locations: Array.isArray(filter.locations) ? filter.locations : 
    (filter.locations ? filter.locations.split(",").filter(Boolean) : []),
  areas: Array.isArray(filter.areas) ? filter.areas : 
    (filter.areas ? filter.areas.split(",").filter(Boolean) : []),
  buildings: Array.isArray(filter.buildings) ? filter.buildings : 
    (filter.buildings ? filter.buildings.split(",").filter(Boolean) : []),
  floors: Array.isArray(filter.floors) ? filter.floors : 
    (filter.floors ? filter.floors.split(",").filter(Boolean) : []),
  zones: Array.isArray(filter.zones) ? filter.zones : 
    (filter.zones ? filter.zones.split(",").filter(Boolean) : []),

});

const FilterStream = forwardRef(({ filter = {}, changeFilterHandle }, ref) => {
  const { token } = useRecoilValue(authState);

  // State for dropdown options
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [zones, setZones] = useState([]);
  
  // Loading and error states
  const [loading, setLoading] = useState({
    locations: false,
    areas: false,
    buildings: false,
    floors: false,
    zones: false,
  });
  const [hierarchyError, setHierarchyError] = useState(null);

  const formik = useFormik({
    initialValues: makeInitial(filter),
    enableReinitialize: true,
    onSubmit: (values) => {
      // Clean up the values before submitting
      const cleanedValues = {
        locations: values.locations.filter(Boolean),
        areas: values.areas.filter(Boolean),
        buildings: values.buildings.filter(Boolean),
        floors: values.floors.filter(Boolean),
        zones: values.zones.filter(Boolean),
      };
      
      // Only include non-empty arrays and valid dates
      const finalValues = Object.fromEntries(
        Object.entries(cleanedValues).filter(([key, value]) => {
          if (Array.isArray(value)) return value.length > 0;
          if (key.includes('DateTime')) return dayjs(value).isValid();
          return value != null && value !== '';
        })
      );
      
      changeFilterHandle(finalValues);
      return finalValues;
    },
  });

  // Generic API caller with loading state
  const callAPI = async (endpoint, setter, loadingKey, params = '') => {
    setLoading(prev => ({ ...prev, [loadingKey]: true }));
    setHierarchyError(null);
    
    try {
      const { data } = await axios.get(`${baseURL}${endpoint}${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const key = endpoint.split('/')[0];
      setter(data[loadingKey] || []);
    } catch (error) {
      console.error(`Failed to fetch ${endpoint}:`, error);
      setHierarchyError(`Failed to load ${loadingKey}`);
      setter([]);
    } finally {
      setLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Fetch Locations
  useEffect(() => {
    callAPI('locations/list', setLocations, 'locations');
  }, [token]);

  // Fetch Areas when locations change
  useEffect(() => {
    if (formik.values.locations.length > 0) {
      const locationIds = formik.values.locations.join(",");
      callAPI('areas/list', setAreas, 'areas', `?locations=${locationIds}`);
    } else {
      setAreas([]);
      formik.setFieldValue('areas', []);
    }
  }, [formik.values.locations, token]);

  // Fetch Buildings when areas change
  useEffect(() => {
    if (formik.values.areas.length > 0) {
      const areaIds = formik.values.areas.join(",");
      callAPI('buildings/list', setBuildings, 'buildings', `?areas=${areaIds}`);
    } else {
      setBuildings([]);
      formik.setFieldValue('buildings', []);
    }
  }, [formik.values.areas, token]);

  // Fetch Floors when buildings change
  useEffect(() => {
    if (formik.values.buildings.length > 0) {
      const buildingIds = formik.values.buildings.join(",");
      callAPI('floor-levels/list', setFloors, 'floor_levels', `?buildings=${buildingIds}`);
    } else {
      setFloors([]);
      formik.setFieldValue('floors', []);
    }
 
    
  }, [formik.values.buildings, token]);

  // Fetch Zones when floors change
  useEffect(() => {
    if (formik.values.floors.length > 0) {
      const floorIds = formik.values.floors.join(",");
      callAPI('zones/list', setZones, 'zones', `?floor_levels=${floorIds}`);
    } else {
      setZones([]);
      formik.setFieldValue('zones', []);
    }
  }, [formik.values.floors, token]);

  // Expose submit method to parent
  useImperativeHandle(ref, () => ({
    submit: async () => {
      return await formik.handleSubmit();
    },
  }));

  // Handle checkbox changes
  const handleCheckboxChange = (field, value) => {
    const selected = formik.values[field] || [];
    const updated = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    formik.setFieldValue(field, updated);
  };

  // Handle select/deselect all
  const handleSelectAll = (field, options, valueKey) => {
    const allValues = options.map(opt => opt[valueKey]);
    const currentValues = formik.values[field] || [];
    const allSelected = allValues.every(val => currentValues.includes(val));
    
    formik.setFieldValue(field, allSelected ? [] : allValues);
  };

  // Clear all filters
  const clearAllFilters = () => {
    formik.resetForm({
      values: {
        locations: [],
        areas: [],
        buildings: [],
        floors: [],
        zones: [],

      }
    });
  };

  // Render checkbox group with select all option
  const renderCheckboxGroup = (label, field, options, valueKey = "location", isLoading = false) => {
    const selectedCount = formik.values[field]?.length || 0;
    const totalCount = options.length;
    console.log(`Rendering ${label}: ${selectedCount} selected out of ${totalCount}`);
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography fontWeight={600}>
            {label} {selectedCount > 0 && `(${selectedCount} selected)`}
          </Typography>
          {totalCount > 0 && (
            <Button
              size="small"
              onClick={() => handleSelectAll(field, options, valueKey)}
            >
              {selectedCount === totalCount ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </Stack>
        
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : options.length === 0 ? (
          <Typography variant="body2" color="text.secondary" py={1}>
            No {label.toLowerCase()} available
          </Typography>
        ) : (
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 1,
            maxHeight: 200,
            overflowY: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 1
          }}>
           {options.map((opt) => (
            <FormControlLabel
              key={opt[valueKey]}
              control={
                <Checkbox
                  checked={(formik.values[field] || []).includes(opt[valueKey])}
                  onChange={() => handleCheckboxChange(field, opt[valueKey])}
                  size="small"
                />
              }
              label={opt[valueKey]}
            />
          ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack gap={3} component="form" onSubmit={formik.handleSubmit}>
       
        {/* Location Hierarchy Filters */}
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight={600}>Location Filters</Typography>
            <Button size="small" onClick={clearAllFilters} color="secondary">
              Clear All Filters
            </Button>
          </Stack>

          {hierarchyError && (
            <Alert severity="error" sx={{ mb: 2 }}>{hierarchyError}</Alert>
          )}

          <Stack gap={3}>
            {renderCheckboxGroup("Locations", "locations", locations, "location", loading.locations)}
            
            {formik.values.locations.length > 0 && 
              renderCheckboxGroup("Areas", "areas", areas, "area", loading.areas)}
            
            {formik.values.areas.length > 0 && 
              renderCheckboxGroup("Buildings", "buildings", buildings, "building", loading.buildings)}
            
            {formik.values.buildings.length > 0 && 
              renderCheckboxGroup("Floors", "floors", floors, "floor_level", loading.floors)}
            
            {formik.values.floors.length > 0 && 
              renderCheckboxGroup("Zones", "zones", zones, "zone", loading.zones)}
          </Stack>
        </Box>

        {/* Summary */}
        <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Filter Summary:</strong> {' '}
            {Object.entries(formik.values).map(([key, value]) => {
              if (Array.isArray(value) && value.length > 0) {
                return `${key}: ${value.length} selected`;
              }
              if (key.includes('DateTime') && dayjs(value).isValid()) {
                return `${key}: ${dayjs(value).format('YYYY-MM-DD HH:mm')}`;
              }
              return null;
            }).filter(Boolean).join(', ') || 'No filters applied'}
          </Typography>
        </Box>
      </Stack>
    </LocalizationProvider>
  );
});

export default FilterStream;